import { QueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

// Query Keys for better organization and type safety
export const queryKeys = {
  auth: {
    profile: () => ['auth', 'profile'] as const,
  },
  orders: {
    lists: () => ['orders', 'list'] as const,
    list: (filters?: any) => ['orders', 'list', filters] as const,
    detail: (id: string) => ['orders', 'detail', id] as const,
    byRole: (role: string) => ['orders', 'role', role] as const,
    dashboard: () => ['orders', 'dashboard'] as const,
    recent: (limit?: number) => ['orders', 'recent', limit] as const,
  },
  products: {
    lists: () => ['products', 'list'] as const,
    list: (filters?: any) => ['products', 'list', filters] as const,
    detail: (id: string) => ['products', 'detail', id] as const,
    categories: () => ['products', 'categories'] as const,
    brands: () => ['products', 'brands'] as const,
    lowStock: () => ['products', 'lowStock'] as const,
    stockMovements: (id: string, filters?: any) => ['products', 'stockMovements', id, filters] as const,
  },
  notifications: {
    lists: () => ['notifications', 'list'] as const,
    list: (filters?: any) => ['notifications', 'list', filters] as const,
    unreadCount: () => ['notifications', 'unreadCount'] as const,
  },
  customers: {
    lists: () => ['customers', 'list'] as const,
    list: (filters?: any) => ['customers', 'list', filters] as const,
    detail: (id: string) => ['customers', 'detail', id] as const,
  },
  invoices: {
    lists: () => ['invoices', 'list'] as const,
    list: (filters?: any) => ['invoices', 'list', filters] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
  },
} as const;

// Global error handler for React Query
const globalErrorHandler = (error: any) => {
  console.error('React Query Error:', error);
  
  // Show user-friendly error message
  const errorMessage = error?.response?.data?.error || 
                      error?.message || 
                      'Bir hata oluştu. Lütfen tekrar deneyin.';
  
  toast.error(errorMessage, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

// Create QueryClient with modern configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Global stale time - data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
      
      // Global cache time - data stays in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      
      // Retry failed requests 3 times with exponential backoff
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus (but only if data is stale)
      refetchOnWindowFocus: false,
      
      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Global error handler for mutations
      onError: globalErrorHandler,
      
      // Retry failed mutations once
      retry: 1,
    },
  },
});

// Utility functions for cache management
export const cacheUtils = {
  // Invalidate and refetch all queries
  invalidateAll: () => {
    queryClient.invalidateQueries();
  },
  
  // Invalidate specific query keys
  invalidateQueries: (queryKey: any) => {
    queryClient.invalidateQueries({ queryKey });
  },
  
  // Remove queries from cache
  removeQueries: (queryKey: any) => {
    queryClient.removeQueries({ queryKey });
  },
  
  // Set query data optimistically
  setQueryData: <T>(queryKey: any, data: T) => {
    queryClient.setQueryData(queryKey, data);
  },
  
  // Get query data from cache
  getQueryData: <T>(queryKey: any): T | undefined => {
    return queryClient.getQueryData(queryKey);
  },
  
  // Prefetch query data
  prefetchQuery: async (queryKey: any, queryFn: () => Promise<any>) => {
    await queryClient.prefetchQuery({
      queryKey,
      queryFn,
      staleTime: 5 * 60 * 1000,
    });
  },
};

// Optimistic update helpers
export const optimisticUpdates = {
  // Optimistically update a list item
  updateListItem: <T extends { _id: string }>(
    queryKey: any,
    updatedItem: T
  ) => {
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.map((item: T) => 
          item._id === updatedItem._id ? updatedItem : item
        ),
      };
    });
  },
  
  // Optimistically add item to list
  addListItem: <T>(queryKey: any, newItem: T) => {
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: [newItem, ...old.data],
      };
    });
  },
  
  // Optimistically remove item from list
  removeListItem: <T extends { _id: string }>(
    queryKey: any,
    itemId: string
  ) => {
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.filter((item: T) => item._id !== itemId),
      };
    });
  },
  
  // Optimistically update pagination
  updatePagination: (queryKey: any, newPagination: any) => {
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!old) return old;
      return {
        ...old,
        pagination: newPagination,
      };
    });
  },
};

// Custom hooks for common patterns
export const useQueryClient = () => queryClient;

// Export for use in other files
export default queryClient; 